import { AxiosError } from 'axios';
import { toaster } from '@halo/ui-chat-react';
import { TypedEventTarget } from '@halo/utils';
import { userApis } from '@/configs/apis/user';
import Paths from '@/configs/router/paths';
import { i18n } from '@/store/i18n/i18n';
import requests_, { instances } from './requests';
import type { MyRequests, MyAxiosResponseData, MyAxiosResponse } from './types';

export const requests = Object.assign(requests_ as MyRequests, {
  event: new TypedEventTarget<{ error: AxiosError<MyAxiosResponseData, any> }>(),
});

for (const k in instances) {
  const instance = instances[k as keyof typeof instances];
  const {
    interceptors: { request, response },
  } = instance;

  // Request handler
  request.use(
    (config) => {
      const token = userApis.getToken();
      config.headers = Object.assign(
        {
          Authorization: 'Bearer ' + token,
          'X-Client-Version': import.meta.env.VITE_HTTP_ENCRYPT_VERSION,
          'X-Encrypt': 'false',
        },
        config.headers
      );
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response handler
  response.use((response: MyAxiosResponse) => {
    const {
      data,
      config,
      request,
      data: { code, message: msg },
      config: { msg: msgArg, disableOnyResponseData },
    } = response;

    // pushLog('response:', response.data);

    if (data && code !== 1) {
      throw new AxiosError<MyAxiosResponseData>(msg, String(code), config, request, response);
    }

    // Success message
    if (msgArg) {
      toaster.success({
        title: typeof msgArg === 'string' ? msgArg : msg || i18n.t('tips.success'),
      });
    }
    // Only response data

    return disableOnyResponseData ? response : (data as any);
  });

  response.use(null, (error: AxiosError<MyAxiosResponseData>) => {
    const response = error.response as MyAxiosResponse;
    if (response) {
      const {
        status,
        data,
        config: { disableErrorEvent, disableErrorMsg },
      } = response;
      // Error event
      if (!disableErrorEvent) {
        requests.event.dispatchEvent('error', error);
      }
      // Error msg
      if (!disableErrorMsg) {
        switch (status) {
          case 401:
            toaster.error({
              title: i18n.t('errors.unauthorized'),
            });
            userApis.removeUserStorage();
            window.location.href = Paths.LOGIN;
            break;
          case 403:
            toaster.error({
              title: i18n.t('errors.noPermission'),
            });
            break;
          default:
            toaster.error({
              title: data?.message
                ? data.message
                : error.message || i18n.t('errors.unknownNetworkError'),
            });

            break;
        }
      }
    }
    return Promise.reject(error);
  });
}

export default requests;
